import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Star,
  Images,
  HelpCircle,
  Settings,
  FileText,
  LogOut,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState({
    totalLeads: 0,
    pendingLeads: 0,
    publishedReviews: 0,
    publishedGallery: 0,
    publishedFAQs: 0,
  });

  useEffect(() => {
    // Get current user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    // Fetch dashboard stats
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch leads count
      const { count: leadsCount } = await (supabase as any)
        .from("leads")
        .select("*", { count: "exact", head: true });

      // Fetch pending leads count
      const { count: pendingCount } = await (supabase as any)
        .from("leads")
        .select("*", { count: "exact", head: true })
        .eq("webhook_status", "pending");

      // Fetch published reviews count
      const { count: reviewsCount } = await (supabase as any)
        .from("reviews")
        .select("*", { count: "exact", head: true })
        .eq("publish", true);

      // Fetch published gallery count
      const { count: galleryCount } = await (supabase as any)
        .from("gallery_images")
        .select("*", { count: "exact", head: true })
        .eq("publish", true);

      // Fetch published FAQs count
      const { count: faqsCount } = await (supabase as any)
        .from("faqs")
        .select("*", { count: "exact", head: true })
        .eq("publish", true);

      setStats({
        totalLeads: leadsCount || 0,
        pendingLeads: pendingCount || 0,
        publishedReviews: reviewsCount || 0,
        publishedGallery: galleryCount || 0,
        publishedFAQs: faqsCount || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
      navigate("/admin/login");
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold text-foreground">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              {user?.email}
            </span>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
              <Mail className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalLeads}</div>
              <p className="text-xs text-muted-foreground">
                {stats.pendingLeads} pending webhook
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Published Reviews</CardTitle>
              <Star className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.publishedReviews}</div>
              <p className="text-xs text-muted-foreground">
                Visible on homepage
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Gallery Images</CardTitle>
              <Images className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.publishedGallery}</div>
              <p className="text-xs text-muted-foreground">
                Published images
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link to="/admin/leads">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Manage Leads
                  </CardTitle>
                  <CardDescription>
                    View and manage lead submissions
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/admin/reviews">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    Manage Reviews
                  </CardTitle>
                  <CardDescription>
                    Add and edit customer reviews
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/admin/gallery">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Images className="w-5 h-5" />
                    Manage Gallery
                  </CardTitle>
                  <CardDescription>
                    Upload and organize gallery images
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/admin/faqs">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    Manage FAQs
                  </CardTitle>
                  <CardDescription>
                    Edit frequently asked questions
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/admin/seo">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    SEO Settings
                  </CardTitle>
                  <CardDescription>
                    Update page titles and meta descriptions
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/admin/settings">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Settings
                  </CardTitle>
                  <CardDescription>
                    Configure tracking and integrations
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          </div>
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Welcome to the Admin Dashboard</CardTitle>
            <CardDescription>
              Manage your website content, view leads, and configure settings from here.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>• View and respond to customer inquiries in the Leads section</p>
              <p>• Add customer testimonials to build trust and credibility</p>
              <p>• Upload project photos to showcase your work</p>
              <p>• Keep your FAQ section up-to-date with common questions</p>
              <p>• Optimize SEO settings for better search visibility</p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
